import React, { useEffect, useState, useMemo } from 'react'
import classes from './index.module.css'
import Loader from '../../../Components/GeneralComponents/Loader'
import { Wrapper } from '../../../Components/page/Wrapper'
import { Header } from '../../../Components/page/Header'
import { HeaderInfo } from '../../../Components/page/HeaderInfo'
import PageTitle from '../components/PageTitle'
import { useParams } from 'react-router-dom'
import { useGetOrgDetails } from '../hooks/useGetOrgDetails'
import { Navigation } from '../../../Components/page/Navigation'
import { renderBreadcrumbs } from './breadcrumbs'
import { useGetOrgContent } from '../hooks/useGetOrgContent'
import OrganizationSortedArtistsList from '../components/OrganizationSortedArtistsList'
import CreateOrganizationPopupArtistsCount from '../components/CreateOrganizationPopupArtistsCount'
import { useInView } from 'react-intersection-observer'
import { Actions } from '../../../Components/page/Actions'
import { ButtonOutline } from '../../../Components/GeneralComponents/ButtonOutline/ButtonOutline'
import usePopup from '../hooks/usePopup'
import ManageOrganizationArtistsPopupForm from '../components/ManageOrganizationArtistsPopupForm'
import { useAuth } from '../../../Contexts/AuthContext'
import useAccessLevel from '../hooks/useAccessLevel'
import DebounceSearch from '../../../Components/GeneralComponents/DebounceSearch/DebounceSearch'
import { useOrganizationArtistsStore } from '../../../Storage/organizationsPage/useOrganizationArtistsStore'

export default function OrganizationArtists() {
  const { id } = useParams()
  const [ref, inView] = useInView()
  const { isInternalAdmin } = useAccessLevel()

  const {
    openPopup: manageArtistsOpenPopup,
    closePopup: manageArtistsClosePopup,
    isOpen: manageArtistsIsOpen,
  } = usePopup()

  const {
    data: orgDetails,
    isError: orgDetailsError,
    isLoading: orgDetailsIsLoading,
    isFetching: orgDetailsFetching,
  } = useGetOrgDetails({
    id,
  })

  const {
    data: orgContent,
    isError: orgContentError,
    isLoading: orgContentIsLoading,
    isFetching: orgContentIsFetching,
    fetchNextPage: orgContentFetchNextPage,
    hasNextPage: orgContentHasNextPage,
  } = useGetOrgContent({
    o_f_id: id,
    content_object: 'artist',
    n: 15,
  })

  const searchTerm = useOrganizationArtistsStore(state => state.searchTerm)
  const setSearchTerm = useOrganizationArtistsStore(
    state => state.setSearchTerm
  )

  const inputValue = useOrganizationArtistsStore(state => state.inputValue)
  const setInputValue = useOrganizationArtistsStore(
    state => state.setInputValue
  )

  const artists = useMemo(
    () =>
      (orgContent?.pages ?? []).reduce((acc, page) => {
        return [...acc, ...page.artist]
      }, []),
    [orgContent]
  )
  const allArtists = orgContent?.pages[0]?.artist_count || 0

  const [filteredArtists, setFilteredArtists] = useState([])

  useEffect(() => {
    if (searchTerm) {
      const term = searchTerm.toLowerCase()
      const filtered = artists.filter(u => u.name?.toLowerCase().includes(term))

      setFilteredArtists(filtered)
    } else {
      setFilteredArtists(artists)
    }
  }, [searchTerm, artists])

  useEffect(() => {
    if (inView && orgContentHasNextPage) {
      orgContentFetchNextPage()
    }
  }, [inView, orgContentHasNextPage, orgContent?.pages?.length])

  if (orgDetailsIsLoading || orgContentIsLoading) {
    return <Loader isLoading={orgDetailsIsLoading || orgContentIsLoading} />
  }

  if (orgDetailsError || orgContentError) {
    return (
      <Wrapper className={classes.wrapper}>
        <Header>
          <HeaderInfo>
            <PageTitle title={'Error loading data'} />
          </HeaderInfo>
        </Header>
      </Wrapper>
    )
  }

  return (
    <Wrapper className={classes.wrapper}>
      <ManageOrganizationArtistsPopupForm
        closePopup={manageArtistsClosePopup}
        organization={orgDetails}
        isOpenPopup={manageArtistsIsOpen}
      />
      <Header>
        {orgDetails ? (
          <Navigation breadcrumbs={renderBreadcrumbs(orgDetails)} />
        ) : null}
        <HeaderInfo>
          <PageTitle title={orgDetails?.name} />
          <Actions>
            {isInternalAdmin && (
              <ButtonOutline
                color={'pink'}
                size="medium"
                text={'MANAGE ARTISTS'}
                onClick={manageArtistsOpenPopup}
              />
            )}
          </Actions>
        </HeaderInfo>
        <div className={classes.section}>
          <div className={classes.section__header}>
            <h2 className={classes.section__title}>Associated Artists</h2>
            <div className={classes.section__search}>
              <DebounceSearch
                inputValue={inputValue}
                setInputValue={setInputValue}
                searchValue={searchTerm}
                setSearchValue={setSearchTerm}
                placeholder={'Search by Artist'}
              />
            </div>
          </div>

          <CreateOrganizationPopupArtistsCount count={allArtists} />
        </div>
      </Header>
      <main className={classes.main}>
        {filteredArtists?.length ? (
          <OrganizationSortedArtistsList
            artists={filteredArtists}
            refetchRef={ref}
          />
        ) : (
          <p className={classes.noResults}>
            No results found. Please refine your search.
          </p>
        )}
      </main>
    </Wrapper>
  )
}
